// DeepFry.js
import React from "react";

export default function DeepFry({
	canvasRef,
	layers,
	drawCanvas,
	bgRect,
	uploadedImageObj,
	preDeepFryCanvasRef,
	preDeepFryLayersRef,
	originalBgDataRef,
	satValue,
	setSatValue,
	conValue,
	setConValue,
	briValue,
	setBriValue,
	hueValue,
	setHueValue,
	pixValue,
	setPixValue, // (CHANGED) we will allow 0
	noiseValue,
	setNoiseValue,
	satEnabled,
	setSatEnabled,
	conEnabled,
	setConEnabled,
	briEnabled,
	setBriEnabled,
	hueEnabled,
	setHueEnabled,
	pixEnabled,
	setPixEnabled,
	noiseEnabled,
	setNoiseEnabled,
	hasBackground,
	hasDeepFried,
	setHasDeepFried,
}) {
	// Helper clamp
	const clampValue = (val, min, max) => Math.max(min, Math.min(val, max));

	const increment = (currentValue, setter, step, max) => {
		setter((prev) => clampValue(prev + step, 0, max));
	};
	const decrement = (currentValue, setter, step, min) => {
		setter((prev) => clampValue(prev - step, min, Number.MAX_VALUE));
	};

	// ========== Pixelate (CHANGED) ==========
	function pixelate(canvas, pixelSize = 4) {
		// If pixelSize <= 0, no effect
		if (pixelSize <= 0) return;

		const ctx = canvas.getContext("2d");
		const offCanvas = document.createElement("canvas");
		const offCtx = offCanvas.getContext("2d");

		offCanvas.width = Math.floor(canvas.width / pixelSize);
		offCanvas.height = Math.floor(canvas.height / pixelSize);
		offCtx.drawImage(canvas, 0, 0, offCanvas.width, offCanvas.height);

		ctx.clearRect(0, 0, canvas.width, canvas.height);
		ctx.imageSmoothingEnabled = false;

		ctx.drawImage(
			offCanvas,
			0,
			0,
			offCanvas.width,
			offCanvas.height,
			0,
			0,
			canvas.width,
			canvas.height
		);
	}

	// ======== addNoise ========
	function addNoise(canvas, amount = 50) {
		const ctx = canvas.getContext("2d");
		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
		const data = imageData.data;

		for (let i = 0; i < data.length; i += 4) {
			const noise = (Math.random() - 0.5) * (amount * 2);
			data[i] = clamp255(data[i] + noise);
			data[i + 1] = clamp255(data[i + 1] + noise);
			data[i + 2] = clamp255(data[i + 2] + noise);
			// alpha unchanged
		}
		ctx.putImageData(imageData, 0, 0);

		function clamp255(v) {
			return Math.min(255, Math.max(0, v));
		}
	}

	// ======== Optional small blur to smooth out leftover banding ========
	function lightBlur(canvas, blurRadius = 0.5) {
		const ctx = canvas.getContext("2d");
		const { width, height } = canvas;

		const tempCanvas = document.createElement("canvas");
		tempCanvas.width = width;
		tempCanvas.height = height;
		const tempCtx = tempCanvas.getContext("2d");

		// Copy the current image
		tempCtx.drawImage(canvas, 0, 0, width, height);

		// Apply a small blur filter, then draw back
		ctx.clearRect(0, 0, width, height);
		ctx.filter = `blur(${blurRadius}px)`;
		ctx.drawImage(tempCanvas, 0, 0, width, height);
		ctx.filter = "none";
	}

	async function handleDeepFry() {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");

		// Deselect
		layers.current.forEach((layer) => {
			layer.isSelected = false;
		});
		drawCanvas();

		// Save pre-fry once
		if (!hasDeepFried) {
			try {
				preDeepFryCanvasRef.current = canvas.toDataURL("image/png");
			} catch (err) {
				console.error("[DeepFry] Error capturing preDeepFry:", err);
			}
			preDeepFryLayersRef.current = layers.current.map((layer) => {
				if (layer.type === "asset") {
					return {
						...layer,
						imageSrc: layer.image?.src,
						image: undefined,
					};
				} else {
					return { ...layer };
				}
			});
		}

		const { x, y, width, height } = bgRect.current || {};
		if (width <= 0 || height <= 0) return;

		const offCanvas = document.createElement("canvas");
		offCanvas.width = width;
		offCanvas.height = height;
		const offCtx = offCanvas.getContext("2d");

		offCtx.drawImage(canvas, x, y, width, height, 0, 0, width, height);

		// ========== SINGLE PASS AT HIGHER QUALITY ==========
		try {
			const dataUrl = offCanvas.toDataURL("image/jpeg", 0.3);
			const tempImg = new Image();
			tempImg.crossOrigin = "anonymous";
			const loaded = new Promise((res) => {
				tempImg.onload = () => res(true);
				tempImg.onerror = () => res(false);
			});
			tempImg.src = dataUrl;
			const success = await loaded;
			if (!success) {
				console.error("[DeepFry] Single-pass tempImg failed to load.");
			}

			offCtx.clearRect(0, 0, width, height);

			const finalSaturate = satEnabled ? satValue : 1;
			const finalContrast = conEnabled ? conValue : 1;
			const finalBright = briEnabled ? briValue : 1;
			const finalHue = hueEnabled ? hueValue : 0;

			// Apply user’s filters
      offCtx.filter = `
      saturate(${finalSaturate})
      contrast(${finalContrast})
      brightness(${finalBright})
      hue-rotate(${finalHue}deg)
    `;
			offCtx.drawImage(tempImg, 0, 0, width, height);
			offCtx.filter = "none";
		} catch (err) {
			console.error("[DeepFry] Single-pass error:", err);
		}

		// ========== (CHANGED) Apply Pixelation IF > 0 ==========
    if (pixEnabled && pixValue > 0) {
      pixelate(offCanvas, pixValue);
    }

		// Add Noise
    if (noiseEnabled && noiseValue > 0) {
      addNoise(offCanvas, noiseValue);
    }

		// Light Blur (optional)
		lightBlur(offCanvas, 0.4);

		// Put final back on main
		ctx.save();
		ctx.clearRect(x, y, width, height);
		ctx.drawImage(offCanvas, 0, 0, width, height, x, y, width, height);
		ctx.restore();

		// Export final
		try {
			const finalDataUrl = canvas.toDataURL("image/png");
			const newImg = new Image();
			newImg.crossOrigin = "anonymous";
			newImg.onload = () => {
				uploadedImageObj.current = newImg;
				drawCanvas();
			};
			newImg.src = finalDataUrl;
		} catch (err) {
			console.error("[DeepFry] Error exporting final PNG:", err);
		}

		layers.current = [];
		drawCanvas();
		setHasDeepFried(true);
	}

	function handleResetDeepFry() {
		if (!originalBgDataRef?.current) return;
		if (!preDeepFryCanvasRef?.current) return;

		const restoredImg = new Image();
		restoredImg.crossOrigin = "anonymous";
		restoredImg.onload = () => {
			uploadedImageObj.current = restoredImg;

			const newLayers = preDeepFryLayersRef.current.map((savedLayer) => {
				if (savedLayer.type === "asset") {
					const newImg = new Image();
					newImg.crossOrigin = "anonymous";
					newImg.onload = () => {
						drawCanvas();
					};
					newImg.src = savedLayer.imageSrc;
					return { ...savedLayer, image: newImg };
				} else {
					return { ...savedLayer };
				}
			});

			layers.current = newLayers;
			setHasDeepFried(false);

			drawCanvas();
			requestAnimationFrame(() => {
				drawCanvas();
			});
		};
		restoredImg.src = originalBgDataRef.current;
	}

	function handleDeepFryDefaults() {
		setSatValue(2.0);
		setConValue(2.0);
		setBriValue(2.4);
		setHueValue(-5);
		setPixValue(0); // (CHANGED) Default to 0 so no pixelation initially
		setNoiseValue(40);

		setSatEnabled(true);
		setConEnabled(true);
		setBriEnabled(true);
		setHueEnabled(true);
		setPixEnabled(true);
		setNoiseEnabled(true);
	}

	function handleEnableAll() {
		setSatEnabled(true);
		setConEnabled(true);
		setBriEnabled(true);
		setHueEnabled(true);
		setPixEnabled(true);
		setNoiseEnabled(true);
	}
	function handleDisableAll() {
		setSatEnabled(false);
		setConEnabled(false);
		setBriEnabled(false);
		setHueEnabled(false);
		setPixEnabled(false);
		setNoiseEnabled(false);
	}

  return (
    <div className="flex flex-col gap-4">
      {/* Sliders in a 3-row x 2-column grid */}
      <div className="grid grid-cols-2 gap-y-6 gap-x-10 ml-4">
        {/* Saturation */}
        <div className="flex flex-col items-center">
          <label className="text-monadWhite text-center mb-1">
            Saturation: {satValue.toFixed(2)}
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="mr-1"
              checked={satEnabled}
              onChange={(e) => setSatEnabled(e.target.checked)}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => decrement(satValue, setSatValue, 0.1, 0)}
              disabled={!satEnabled}
            >
              -
            </button>
            <input
              type="range"
              className="w-20"
              min={0}
              max={5}
              step={0.1}
              value={satValue}
              onChange={(e) => setSatValue(parseFloat(e.target.value))}
              disabled={!satEnabled}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => increment(satValue, setSatValue, 0.1, 5)}
              disabled={!satEnabled}
            >
              +
            </button>
          </div>
        </div>

        {/* Contrast */}
        <div className="flex flex-col items-center">
          <label className="text-monadWhite text-center mb-1">
            Contrast: {conValue.toFixed(2)}
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="mr-1"
              checked={conEnabled}
              onChange={(e) => setConEnabled(e.target.checked)}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => decrement(conValue, setConValue, 0.1, 0)}
              disabled={!conEnabled}
            >
              -
            </button>
            <input
              type="range"
              className="w-20"
              min={0}
              max={5}
              step={0.1}
              value={conValue}
              onChange={(e) => setConValue(parseFloat(e.target.value))}
              disabled={!conEnabled}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => increment(conValue, setConValue, 0.1, 5)}
              disabled={!conEnabled}
            >
              +
            </button>
          </div>
        </div>

        {/* Brightness */}
        <div className="flex flex-col items-center">
          <label className="text-monadWhite text-center mb-1">
            Brightness: {briValue.toFixed(2)}
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="mr-1"
              checked={briEnabled}
              onChange={(e) => setBriEnabled(e.target.checked)}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => decrement(briValue, setBriValue, 0.1, 0)}
              disabled={!briEnabled}
            >
              -
            </button>
            <input
              type="range"
              className="w-20"
              min={0}
              max={5}
              step={0.1}
              value={briValue}
              onChange={(e) => setBriValue(parseFloat(e.target.value))}
              disabled={!briEnabled}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => increment(briValue, setBriValue, 0.1, 5)}
              disabled={!briEnabled}
            >
              +
            </button>
          </div>
        </div>

        {/* Hue */}
        <div className="flex flex-col items-center">
          <label className="text-monadWhite text-center mb-1">
            Hue: {hueValue}
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="mr-1"
              checked={hueEnabled}
              onChange={(e) => setHueEnabled(e.target.checked)}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => decrement(hueValue, setHueValue, 1, -100)}
              disabled={!hueEnabled}
            >
              -
            </button>
            <input
              type="range"
              className="w-20"
              min={-100}
              max={360}
              step={1}
              value={hueValue}
              onChange={(e) => setHueValue(parseInt(e.target.value, 10))}
              disabled={!hueEnabled}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => increment(hueValue, setHueValue, 1, 360)}
              disabled={!hueEnabled}
            >
              +
            </button>
          </div>
        </div>

        {/* Pixelation */}
        <div className="flex flex-col items-center">
          <label className="text-monadWhite text-center mb-1">
            Pixelation: {pixValue}
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="mr-1"
              checked={pixEnabled}
              onChange={(e) => setPixEnabled(e.target.checked)}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => decrement(pixValue, setPixValue, 1, 0)}
              disabled={!pixEnabled}
            >
              -
            </button>
            <input
              type="range"
              className="w-20"
              min={0}
              max={20}
              step={1}
              value={pixValue}
              onChange={(e) => setPixValue(parseInt(e.target.value, 10))}
              disabled={!pixEnabled}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => increment(pixValue, setPixValue, 1, 20)}
              disabled={!pixEnabled}
            >
              +
            </button>
          </div>
        </div>

        {/* Noise */}
        <div className="flex flex-col items-center">
          <label className="text-monadWhite text-center mb-1">
            Noise: {noiseValue}
          </label>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              className="mr-1"
              checked={noiseEnabled}
              onChange={(e) => setNoiseEnabled(e.target.checked)}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => decrement(noiseValue, setNoiseValue, 5, 0)}
              disabled={!noiseEnabled}
            >
              -
            </button>
            <input
              type="range"
              className="w-20"
              min={0}
              max={100}
              step={5}
              value={noiseValue}
              onChange={(e) => setNoiseValue(parseInt(e.target.value, 10))}
              disabled={!noiseEnabled}
            />
            <button
              className="px-2 bg-monadAccent text-monadWhite rounded"
              onClick={() => increment(noiseValue, setNoiseValue, 5, 100)}
              disabled={!noiseEnabled}
            >
              +
            </button>
          </div>
        </div>
      </div>

      {/* First row of action buttons */}
      <div className="flex gap-2 mt-4 flex-wrap">
        <button
          onClick={handleDeepFry}
          disabled={!hasBackground}
          className="
            text-sm md:text-base px-4 py-2 rounded-md bg-red-600 text-monadWhite
            hover:bg-red-800 transition-colors
            disabled:opacity-60 disabled:cursor-not-allowed
          "
        >
          Deep Fry
        </button>

        <button
          onClick={handleResetDeepFry}
          disabled={!hasBackground || !hasDeepFried}
          className="
            text-sm md:text-base px-4 py-2 rounded-md bg-red-600 text-monadWhite
            hover:bg-red-800 transition-colors
            disabled:opacity-60 disabled:cursor-not-allowed
          "
        >
          Remove Fry
        </button>

        <button
          onClick={handleDeepFryDefaults}
          className="
            text-sm md:text-base px-4 py-2 rounded-md bg-red-600 text-monadWhite
            hover:bg-gray-700 transition-colors
          "
        >
          Restore Default
        </button>
      </div>

      {/* Second row for Enable/Disable All */}
      <div className="flex gap-2 mt-2 flex-wrap">
        <button
          onClick={handleEnableAll}
          className="
            text-sm md:text-base px-4 py-2 rounded-md bg-green-600 text-white
            hover:bg-green-800 transition-colors
          "
        >
          Enable All
        </button>

        <button
          onClick={handleDisableAll}
          className="
            text-sm md:text-base px-4 py-2 rounded-md bg-gray-600 text-white
            hover:bg-gray-800 transition-colors
          "
        >
          Disable All
        </button>
      </div>
    </div>
  );
}
